import React, { useState, useCallback } from 'react';
import { Turtle, Shield, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import ForgotPasswordForm from '../components/auth/ForgotPasswordForm';
import ResetPasswordForm from '../components/auth/ResetPasswordForm';
import EmailVerificationForm from '../components/auth/EmailVerificationForm';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getTenantConfig, isWhiteLabel } from '../config/tenantConfig';
import authService from '../services/authService';

const AuthPage: React.FC = () => {
  const { login, register, forgotPassword, resetPassword, isLoading, error, clearError } = useAuth() as any;
  const tenantConfig = getTenantConfig();
  const whiteLabel = isWhiteLabel();
  const [oauthLoading, setOauthLoading] = useState(false);
  const [betaBlocked, setBetaBlocked] = useState(false);
  const [betaMessage, setBetaMessage] = useState('');

  const handleZebuOAuthLogin = useCallback(async () => {
    try {
      setOauthLoading(true);
      clearError();

      const callbackUrl = `${window.location.origin}/oauth/callback`;
      const { oauth_url, state: oauthState } = await authService.getZebuOAuthUrl(callbackUrl);

      // Store state for validation
      localStorage.setItem('oauth_state', oauthState);
      localStorage.setItem('oauth_broker', 'zebu');
      localStorage.setItem('oauth_mode', 'whitelabel');

      // Open popup
      const popup = window.open(
        `${oauth_url}&redirect_uri=${encodeURIComponent(callbackUrl)}`,
        'zebu-oauth',
        'width=500,height=650,scrollbars=yes,resizable=yes'
      );

      // Listen for callback message
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'ZEBU_LOGIN_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          setOauthLoading(false);
          // Popup stored tokens in localStorage — reload the page to pick them up
          window.location.href = '/';
        } else if (event.data?.type === 'ZEBU_LOGIN_ERROR') {
          window.removeEventListener('message', handleMessage);
          setOauthLoading(false);
          if (event.data?.error === 'beta_access_denied') {
            setBetaBlocked(true);
            setBetaMessage(event.data.message || 'This platform is currently in beta. Your account does not have access yet.');
          }
        }
      };
      window.addEventListener('message', handleMessage);

      // Monitor popup closure
      const checkPopup = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkPopup);
          setOauthLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      }, 1000);

    } catch (err) {
      setOauthLoading(false);
    }
  }, [clearError]);
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'forgot-password' | 'reset-password' | 'verify-email'>('login');
  
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string>('');
  const [pendingResetEmail, setPendingResetEmail] = useState<string>('');

  const handleLogin = async (credentials: any) => {
    try {
      await login(credentials);
    } catch (error) {
      // Error is handled by AuthContext
    }
  };

  const handleRegister = async (userData: any) => {
    try {
      const response = await register(userData);
      
      // Check if email verification is required
      if (response?.email_verification_required === true) {
        // After successful registration, redirect to verification
        setPendingVerificationEmail(userData.email);
        setAuthMode('verify-email');
      } else {
        // If no verification needed, show success and go to login
        setRegistrationSuccess(true);
        setAuthMode('login');
      }
    } catch (error) {
      // Error is handled by AuthContext
    }
  };

  const switchToRegister = () => {
    clearError();
    setRegistrationSuccess(false);
    setAuthMode('register');
  };

  const switchToLogin = () => {
    clearError();
    setRegistrationSuccess(false);
    setForgotPasswordSuccess(false);
    setResetPasswordSuccess(false);
    setPendingVerificationEmail('');
    setPendingResetEmail('');
    setAuthMode('login');
  };

  const switchToForgotPassword = () => {
    clearError();
    setRegistrationSuccess(false);
    setForgotPasswordSuccess(false);
    setResetPasswordSuccess(false);
    setAuthMode('forgot-password');
  };

  const switchToResetPassword = () => {
    clearError();
    setAuthMode('reset-password');
  };

  const handleVerificationSuccess = () => {
    // After successful email verification, show success message and go to login
    setRegistrationSuccess(true);
    setPendingVerificationEmail('');
    setAuthMode('login');
  };

  const handleForgotPasswordSubmit = async (email: string) => {
    try {
      await forgotPassword(email);
      setPendingResetEmail(email);
      setForgotPasswordSuccess(true);
    } catch (error) {
      // Error is handled by AuthContext
    }
  };

  const handleResetPasswordSubmit = async (email: string, code: string, newPassword: string) => {
    try {
      await resetPassword(email, code, newPassword);
      setResetPasswordSuccess(true);
    } catch (error) {
      // Error is handled by AuthContext
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-sm mx-auto mt-20">
        <LoadingSpinner message="Authenticating..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center py-4">
      <div className="w-full max-w-md px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            {whiteLabel ? (
              <img src="/zebu-logo.svg" alt={tenantConfig.name} className="h-12 w-auto mr-3" />
            ) : (
              <>
                <Turtle className="h-12 w-12 text-blue-600 mr-3" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {tenantConfig.name}
                </h1>
              </>
            )}
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            {tenantConfig.tagline}
          </p>
        </div>

        {/* Registration Success Message */}
        {!whiteLabel && registrationSuccess && (
          <div className="mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                    <strong>Registration Successful!</strong>
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Your account has been created and email verified successfully. You can now sign in to continue to your dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Zebu White-Label OAuth Login */}
        {whiteLabel && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <button
              onClick={handleZebuOAuthLogin}
              disabled={oauthLoading}
              className="w-full flex items-center justify-center gap-3 bg-zebu-600 hover:bg-zebu-700 disabled:bg-zebu-400 text-white font-semibold py-3.5 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {oauthLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Connecting...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Login with Zebu
                </>
              )}
            </button>
            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-400 dark:text-gray-500">
              <Shield className="h-3.5 w-3.5 text-zebu-500" />
              Secure OAuth via go.mynt.in
            </div>

            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            {betaBlocked && (
              <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">Beta Access Required</p>
                <p className="text-sm text-amber-700 dark:text-amber-300">{betaMessage}</p>
              </div>
            )}
          </div>
        )}

        {/* Standard Auth Forms (non white-label only) */}
        {!whiteLabel && (
          authMode === 'login' ? (
            <LoginForm
              onSubmit={handleLogin}
              onRegisterClick={switchToRegister}
              onForgotPasswordClick={switchToForgotPassword}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
            />
          ) : authMode === 'register' ? (
            <RegisterForm
              onSubmit={handleRegister}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
            />
          ) : authMode === 'verify-email' ? (
            <EmailVerificationForm
              email={pendingVerificationEmail}
              onBack={switchToLogin}
              onVerificationSuccess={handleVerificationSuccess}
            />
          ) : authMode === 'reset-password' ? (
            <ResetPasswordForm
              email={pendingResetEmail}
              onSubmit={handleResetPasswordSubmit}
              onBackToForgotPassword={resetPasswordSuccess ? switchToLogin : switchToForgotPassword}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
              success={resetPasswordSuccess}
            />
          ) : (
            <ForgotPasswordForm
              onSubmit={handleForgotPasswordSubmit}
              onBackToLogin={switchToLogin}
              onContinueToReset={switchToResetPassword}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
              success={forgotPasswordSuccess}
            />
          )
        )}

        {/* Additional Info for Register Form */}
        {!whiteLabel && authMode === 'register' && (
          <div className="mt-6 text-center">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md p-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                <strong>Why Quantleap Analytics?</strong>
              </p>
              <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <p>• Secure AWS-based infrastructure</p>
                <p>• Indian market specialized algorithms</p>
                <p>• Zerodha integration for seamless trading</p>
                <p>• Advanced risk management tools</p>
              </div>
            </div>
          </div>
        )}

        {/* Switch Auth Mode */}
        {!whiteLabel && authMode === 'register' && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Already have an account?{' '}
              <button
                className="text-blue-600 dark:text-blue-400 underline font-semibold hover:text-blue-700 dark:hover:text-blue-300"
                onClick={switchToLogin}
              >
                Sign In Here
              </button>
            </p>
          </div>
        )}

        {/* Footer */}
        {!whiteLabel && (
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Module 2: User Authentication & Broker Management
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;